import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";

import RoutineCard, {
  RoutineCardRoutine,
} from "@apps/account/components/skills/routine-card";
import RoutineEditorModal, {
  RoutineEditorInitial,
} from "@apps/account/components/skills/routine-editor-modal";
import routineSkillOptionsQuery from "@apps/account/graphql/routine-skill-options-query";
import routinesQuery from "@apps/account/graphql/routines-query";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Button from "@components/button/button";
import GraphqlError from "@components/error/graphql-error";
import Heading from "@components/heading/heading";
import Loading from "@components/loading/loading";

type SkillOption = { id: string; name: string; slug: string };

const RoutinesList = () => {
  const label = useLabel();
  const organizationId = currentOrganizationVar().id;

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<RoutineEditorInitial | undefined>(
    undefined,
  );

  const { data, loading, error } = useQuery(routinesQuery, {
    fetchPolicy: "cache-and-network",
  });
  const { data: skillData } = useQuery(routineSkillOptionsQuery, {
    variables: { organizationId },
  });

  // Resolve a routine's display name by its unified skill id first, then by slug
  // (legacy routines), so the right name shows when two skills share a slug.
  const skillNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (skillData) {
      const all: SkillOption[] = [
        ...(skillData.mySkills ?? []),
        ...(skillData.workspaceSkills ?? []),
        ...(skillData.platformSkills ?? []),
      ];
      all.forEach((skill) => {
        if (skill?.id) map.set(skill.id, skill.name);
      });
    }
    return map;
  }, [skillData]);

  const skillNameBySlug = useMemo(() => {
    const map = new Map<string, string>();
    if (skillData) {
      const platform: SkillOption[] = skillData.platformSkills ?? [];
      const workspace: SkillOption[] = skillData.workspaceSkills ?? [];
      const personal: SkillOption[] = skillData.mySkills ?? [];
      // Precedence personal > workspace > platform.
      [...platform, ...workspace, ...personal].forEach((skill) => {
        if (skill?.slug) map.set(skill.slug, skill.name);
      });
    }
    return map;
  }, [skillData]);

  const resolveSkillName = (routine: RoutineCardRoutine): string | undefined =>
    (routine.skillId && skillNameById.get(routine.skillId)) ||
    skillNameBySlug.get(routine.skillSlug);

  const routines: RoutineCardRoutine[] = data?.routines ?? [];

  const handleCreate = () => {
    setEditing(undefined);
    setEditorOpen(true);
  };

  const handleEdit = (routine: RoutineCardRoutine) => {
    setEditing({
      id: routine.id,
      name: routine.name,
      skillId: routine.skillId,
      skillSlug: routine.skillSlug,
      triggerType: routine.triggerType,
      triggerConfig: routine.triggerConfig,
      selectedMeetingGroups: routine.selectedMeetingGroups,
    });
    setEditorOpen(true);
  };

  return (
    <div aria-label="Routines page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <Heading small title="Routines" />
        <Button
          theme="primary"
          text="Create Routine"
          aria-label="Create routine"
          onClick={handleCreate}
        />
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Routines run one of your skills on a schedule or around your meetings
        and send the result to you in Slack or Teams.
      </p>

      {error ? (
        <GraphqlError
          error={error}
          whatDidNotWork="Your routines could not be loaded."
        />
      ) : loading && routines.length === 0 ? (
        <Loading mini size={6} />
      ) : routines.length === 0 ? (
        <div className="text-sm text-gray-500 border border-dashed rounded-md p-6 text-center">
          No routines yet. Create one to run a skill automatically.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {routines.map((routine) => (
            <RoutineCard
              key={routine.id}
              routine={routine}
              skillName={resolveSkillName(routine)}
              label={label}
              onEdit={() => handleEdit(routine)}
            />
          ))}
        </div>
      )}

      <RoutineEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        initial={editing}
      />
    </div>
  );
};

export default RoutinesList;
